import React, { useState } from 'react';
import { usePubStyles, PubNav, PubFooter, PubPage } from './PublicLayout';

interface Props {
  onNavigate: (page: PubPage) => void;
}

const OFFICES = [
  { city: 'New York',  role: 'Global HQ',     addr: '1 World Trade Center, Suite 8800\nNew York, NY 10007', phone: '+1 212 555 0100' },
  { city: 'London',   role: 'European HQ',    addr: '30 St Mary Axe (The Gherkin)\nLondon EC3A 8BF, UK',   phone: '+44 20 7946 0100' },
  { city: 'Dubai',    role: 'MENA Office',    addr: 'DIFC Gate Building, Level 14\nDubai, UAE',              phone: '+971 4 555 0100' },
];

const CONTACT_CHANNELS = [
  { icon: '✉',  label: 'General Enquiries',  val: 'info@nationstardigitalbank.com',    sub: 'Response within 24 hours' },
  { icon: '🛡', label: 'Customer Support',   val: 'support@nationstardigitalbank.com', sub: '24/7 · Immediate response' },
  { icon: '⚠', label: 'Report Fraud',       val: 'fraud@nationstardigitalbank.com',   sub: 'Urgent · We respond in minutes' },
  { icon: '📞', label: 'Phone Support',      val: '1-800-NSTAR-01',                   sub: 'Mon–Fri 8am–8pm EST' },
  { icon: '💬', label: 'Live Chat',          val: 'Available in your dashboard',       sub: 'Sign in to access' },
];

export default function ContactPage({ onNavigate }: Props) {
  usePubStyles();

  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'general', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.name || !form.email || !form.message) {
      alert('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setTimeout(() => { setLoading(false); setSent(true); }, 900);
  };

  return (
    <div className="pub-page">
      <PubNav current="contact" onNavigate={onNavigate} />

      {/* ── Hero ── */}
      <div className="pub-wrap" style={{ padding: '80px 60px 40px' }}>
        <div className="pub-eyebrow">◆ Get In Touch</div>
        <h1 className="pub-h1" style={{ marginBottom: 12 }}>We're here to <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>help</em></h1>
        <p className="pub-p" style={{ maxWidth: 540 }}>
          Whether you have a question about your account, want to explore our services, or simply need support —
          our team is available around the clock to assist you.
        </p>
      </div>

      {/* ── Banner image ── */}
      <div className="pub-wrap" style={{ marginBottom: 64 }}>
        <div style={{ borderRadius: 20, overflow: 'hidden', aspectRatio: '21/6', position: 'relative' }}>
          <img
            src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1400&h=400&fit=crop&q=85"
            alt="Nationstar Bank support team"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right,rgba(6,10,20,.8) 0%,transparent 55%)' }} />
          <div style={{ position: 'absolute', top: '50%', left: 60, transform: 'translateY(-50%)' }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 300, marginBottom: 8 }}>Available 24 / 7</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,.7)' }}>Our support team never sleeps so you never have to worry</div>
          </div>
        </div>
      </div>

      {/* ── Main grid ── */}
      <div className="pub-wrap" style={{ marginBottom: 80 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'start' }}>

          {/* Form */}
          <div style={{ background: 'var(--navy2)', border: '1px solid var(--border)', borderRadius: 24, padding: 40 }}>
            {sent ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: 52, marginBottom: 16 }}>✉</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, marginBottom: 10 }}>Message Sent</div>
                <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.75, marginBottom: 28 }}>
                  Thank you, <strong style={{ color: 'var(--text)' }}>{form.name}</strong>. Our team will respond to{' '}
                  <strong style={{ color: 'var(--gold)' }}>{form.email}</strong> within one business day.
                </div>
                <button className="pub-btn-ghost" onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', subject: 'general', message: '' }); }}>
                  Send Another Message
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, marginBottom: 6 }}>Send a Message</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 28 }}>We'll get back to you within one business day.</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Full Name *</label>
                    <input
                      style={{ width: '100%', background: 'var(--navy3)', border: '1px solid var(--border)', borderRadius: 12, padding: '11px 14px', color: 'var(--text)', fontSize: 14, fontFamily: 'DM Sans, sans-serif', outline: 'none' }}
                      placeholder="Your full name"
                      value={form.name} onChange={e => update('name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Email Address *</label>
                    <input
                      type="email"
                      style={{ width: '100%', background: 'var(--navy3)', border: '1px solid var(--border)', borderRadius: 12, padding: '11px 14px', color: 'var(--text)', fontSize: 14, fontFamily: 'DM Sans, sans-serif', outline: 'none' }}
                      placeholder="you@email.com"
                      value={form.email} onChange={e => update('email', e.target.value)}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Phone</label>
                    <input
                      style={{ width: '100%', background: 'var(--navy3)', border: '1px solid var(--border)', borderRadius: 12, padding: '11px 14px', color: 'var(--text)', fontSize: 14, fontFamily: 'DM Sans, sans-serif', outline: 'none' }}
                      placeholder="+1 555 000 0000"
                      value={form.phone} onChange={e => update('phone', e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Subject</label>
                    <select
                      style={{ width: '100%', background: 'var(--navy3)', border: '1px solid var(--border)', borderRadius: 12, padding: '11px 14px', color: 'var(--text)', fontSize: 14, fontFamily: 'DM Sans, sans-serif', outline: 'none', cursor: 'pointer' }}
                      value={form.subject} onChange={e => update('subject', e.target.value)}
                    >
                      <option value="general">General Enquiry</option>
                      <option value="account">Account Support</option>
                      <option value="fraud">Report Fraud</option>
                      <option value="business">Business Banking</option>
                      <option value="press">Press & Media</option>
                      <option value="careers">Careers</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Message *</label>
                  <textarea
                    style={{ width: '100%', background: 'var(--navy3)', border: '1px solid var(--border)', borderRadius: 12, padding: '11px 14px', color: 'var(--text)', fontSize: 14, fontFamily: 'DM Sans, sans-serif', outline: 'none', minHeight: 120, resize: 'vertical' }}
                    placeholder="How can we help you today?"
                    value={form.message} onChange={e => update('message', e.target.value)}
                  />
                </div>

                <button className="pub-btn-gold" style={{ width: '100%', justifyContent: 'center' }} onClick={submit} disabled={loading}>
                  {loading ? 'Sending…' : 'Send Message →'}
                </button>
                <div style={{ fontSize: 11, color: 'var(--dim)', textAlign: 'center', marginTop: 14 }}>
                  🔒 Protected by 256-bit SSL encryption
                </div>
              </>
            )}
          </div>

          {/* Info panel */}
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, marginBottom: 6 }}>Contact Information</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24 }}>Multiple ways to reach us — choose what works best for you.</div>

            <div style={{ background: 'var(--navy2)', border: '1px solid var(--border)', borderRadius: 20, overflow: 'hidden' }}>
              {CONTACT_CHANNELS.map((ch, i) => (
                <div key={ch.label} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '18px 20px', borderBottom: i < CONTACT_CHANNELS.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--golddim)', border: '1px solid rgba(201,168,76,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {ch.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 }}>{ch.label}</div>
                    <div style={{ fontSize: 14, color: 'var(--text)', marginBottom: 2 }}>{ch.val}</div>
                    <div style={{ fontSize: 12, color: 'var(--dim)' }}>{ch.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Offices ── */}
        <div style={{ marginTop: 72 }}>
          <div className="pub-eyebrow">Global Offices</div>
          <h2 className="pub-h2" style={{ marginBottom: 36 }}>Find us <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>worldwide</em></h2>
          <div className="pub-grid-3">
            {OFFICES.map(o => (
              <div key={o.city} className="pub-card">
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, marginBottom: 4 }}>{o.city}</div>
                <div style={{ fontSize: 11, color: 'var(--gold)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 16 }}>{o.role}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.75, whiteSpace: 'pre-line', marginBottom: 14 }}>{o.addr}</div>
                <div style={{ fontSize: 13, color: 'var(--text)' }}>{o.phone}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PubFooter onNavigate={onNavigate} />
    </div>
  );
}
